import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Table, useFetch } from "@4uhub/lib4uhub";
import { Box, Typography } from "@mui/material";
import {
  GridCheckCircleIcon,
  GridColDef,
  GridRowParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";

import MainApi from "../../../../services/mainApi.service";
import { memo, useCallback, useMemo } from "react";
import { useSalesContract } from "../../../../store/contexts/SalesContractContext";
import { ISalesContractHistory } from "../../../../models/sales-contract-history";
import { approveSalesContractHistory } from "../../../../services/salesContractHistory.service";
const translationsPath =
  "page.4uSalesContract.registers.salesContractHistories.";

const serviceURL = process.env.REACT_APP_MAIN + "/api/v1/SalesContractHistory/";
const service = new MainApi<ISalesContractHistory>(serviceURL);

export type TRowClick = GridRowParams<ISalesContractHistory>;

export const SalesContractHistoriesPage = memo(() => {
  const { onOpen, selectPropId, onRefresh, selectTabName, refresh } =
    useSalesContract();

  const { sendRequest: approve } = useFetch(approveSalesContractHistory);

  const { id } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const translate = (field: string) => t(translationsPath + field);

  const columns: GridColDef<ISalesContractHistory>[] = [
    {
      field: "title",
      headerName: translate("titleForm"),
      renderCell: ({ value }) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "history",
      headerName: translate("history"),
      renderCell: ({ value }) => (
        <Typography variant="body2">{value}</Typography>
      ),
    },
    {
      field: "historyDate",
      headerName: translate("history_date"),
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
        return format(date, "P", {
          locale: language === "pt-BR" ? ptBR : enUS,
        });
      },
    },
    {
      field: "approvalDate",
      headerName: translate("approval_date"),
      renderCell: (params) => {
        if (!params.value) return "-";
        const date = new Date(params.value);
        date.setHours(date.getHours() + date.getTimezoneOffset() / 60);
        return format(date, "P", {
          locale: language === "pt-BR" ? ptBR : enUS,
        });
      },
    },
  ];

  const handleAdd = () => {
    onOpen();
    selectTabName("history");
  };

  const rowClickHandler = useCallback(
    (params: GridRowParams<ISalesContractHistory>) => {
      selectPropId(params.row.id);
      onOpen();
      selectTabName("history");
    },
    [selectPropId, onOpen, selectTabName]
  );

  const onApproveSalesContractHistory = useCallback(
    async (id: string) => {
      const { success } = await approve(id);
      if (success) {
        onRefresh();
      }
    },
    [approve, onRefresh]
  );

  const props = useMemo(() => {
    return { SalesContractId: id, refresh };
  }, [id, refresh]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      <Box height={600}>
        <Table<ISalesContractHistory>
          searchInput
          showDefaultMacroFilters={false}
          columns={columns}
          service={service}
          onAdd={handleAdd}
          onRowClick={rowClickHandler}
          serviceProps={props}
          title={translate("title")}
          addButtonLabel={translate("add")}
          searchInputPlaceHolder={translate("search")}
          rowActions={(params) => [
            params.row.approvalDate === null
              ? {
                  action: (params) => {
                    onApproveSalesContractHistory(params.row.id);
                  },
                  icon: <GridCheckCircleIcon fontSize="small" />,
                  text: `${t(translationsPath + "approve")}`,
                }
              : null,
          ]}
          confirmOptions={{
            title: translate("confirm.title"),
            description: translate("confirm.description"),
            confirmationButtonProps: { variant: "contained" },
          }}
        />
      </Box>
    </Box>
  );
});
